
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import {
  CATEGORY_LABELS,
  SHIFT_TEMPLATES,
  type Category,
  type PersonInput,
  type ShiftTemplate,
} from "@/lib/types";

const ALL_CATEGORIES: Category[] = ["YOGA", "PDC", "EMPLOYEE"];

function derivePersonType(categories: Category[]): string {
  if (categories.length === 0) return "unknown";
  if (categories.length === 1) return categories[0].toLowerCase();
  return categories.map((c) => c.toLowerCase()).join("+");
}

export default function PersonFormPage() {
  const params = useParams();
  const id = params.id as string;
  const isNew = id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [devicePin, setDevicePin] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<
    Partial<Record<Category, ShiftTemplate>>
  >({});
  const [active, setActive] = useState(true);

  const { data: person, isLoading } = useQuery({
    queryKey: ["admin", "person", id],
    queryFn: () => adminApi.getPerson(adminPassword!, id),
    enabled: !isNew && !!adminPassword,
  });

  useEffect(() => {
    if (person) {
      setName(person.name);
      setPhone(person.phone);
      setDevicePin(person.device_pin);
      setCategories(person.categories);
      setShiftTemplates(person.shift_templates ?? {});
      setActive(person.active);
    }
  }, [person]);

  const save = useMutation({
    mutationFn: (payload: PersonInput) =>
      isNew
        ? adminApi.createPerson(adminPassword!, payload)
        : adminApi.updatePerson(adminPassword!, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "people"] });
      navigate("/admin/people");
    },
  });

  function toggleCategory(cat: Category) {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    save.mutate({
      name: name.trim(),
      phone: phone.trim(),
      device_pin: devicePin.trim(),
      categories,
      shift_templates: shiftTemplates,
      active,
    });
  }

  if (!isNew && isLoading) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">
          {isNew ? "Add person" : "Edit person"}
        </h2>
        <p className="text-sm text-muted-foreground">
          Derived type: {derivePersonType(categories)}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pin">Device PIN (unique)</Label>
              <Input
                id="pin"
                value={devicePin}
                onChange={(e) => setDevicePin(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Categories</Label>
              <div className="space-y-2">
                {ALL_CATEGORIES.map((cat) => (
                  <div key={cat} className="flex items-center gap-2">
                    <Checkbox
                      id={`cat-${cat}`}
                      checked={categories.includes(cat)}
                      onCheckedChange={() => toggleCategory(cat)}
                    />
                    <Label htmlFor={`cat-${cat}`} className="font-normal">
                      {CATEGORY_LABELS[cat]}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {categories.map((cat) => (
              <div key={cat} className="space-y-2">
                <Label>Shift template — {CATEGORY_LABELS[cat]}</Label>
                <Select
                  value={shiftTemplates[cat] ?? ""}
                  onValueChange={(v) =>
                    setShiftTemplates((prev) => ({
                      ...prev,
                      [cat]: v as ShiftTemplate,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Inherit default" />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIFT_TEMPLATES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="flex items-center gap-2">
              <Checkbox
                id="active"
                checked={active}
                onCheckedChange={(v) => setActive(v === true)}
              />
              <Label htmlFor="active" className="font-normal">
                Active on devices
              </Label>
            </div>

            {save.isError && (
              <p className="text-sm text-red-600">Could not save person.</p>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={save.isPending || categories.length === 0}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/admin/people">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
